// src/services/recipeService.js  —  ohapotato v2026.10.36
//
// BACKEND ABSTRACTION LAYER

// BACKEND is always handled server-side via /api/recipes
const BACKEND = 'edamam';

// ── HTML entity decoder ───────────────────────────────────────────────────────
// Spoonacular (and some other APIs) return HTML-encoded text in step instructions,
// e.g. "&amp;" instead of "&", "&#39;" instead of "'", "&deg;" instead of "°".
// We decode these before storing so they render correctly in React.
function decodeHtml(str) {
  if (!str || typeof str !== 'string') return str;
  return str
    .replace(/&amp;/g,   '&')
    .replace(/&lt;/g,    '<')
    .replace(/&gt;/g,    '>')
    .replace(/&quot;/g,  '"')
    .replace(/&#39;/g,   "'")
    .replace(/&apos;/g,  "'")
    .replace(/&nbsp;/g,  ' ')
    .replace(/&deg;/g,   '°')
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)));
}

// ── Temperature normaliser ────────────────────────────────────────────────────
// Converts any Celsius temperature found in a string to Fahrenheit.
// Handles: "180°C", "180 °C", "180C", "180 degrees C", "180 degrees Celsius"
function toFahrenheit(str) {
  if (!str || typeof str !== 'string') return str;
  return str.replace(
    /(\d+(?:\.\d+)?)\s*(?:°\s*C|degrees?\s+C(?:elsius)?|°C)/gi,
    (_, c) => Math.round(parseFloat(c) * 9 / 5 + 32) + '°F'
  );
}

// Apply both decoders to a step instruction
function cleanInstruction(text) {
  return toFahrenheit(decodeHtml(text));
}

// ── GEMINI (server-side proxy) ────────────────────────────────────────────────
// Calls /api/gemini-recipes — key never touches the browser.
async function fetchFromGemini(ingredients) {
  const res = await fetch('/api/gemini-recipes', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ ingredients }),
  });

  const text = await res.text();
  let data = {};
  try { data = JSON.parse(text); } catch { /* non-JSON */ }

  if (!res.ok) {
    if (res.status === 429) throw new Error('Gemini rate limit — please wait a moment and try again.');
    throw new Error(data.error || `Gemini function error ${res.status}`);
  }

  if (!data.recipes) throw new Error('No recipes returned from Gemini function.');
  return data.recipes.map(recipe => ({
    ...recipe,
    steps: (recipe.steps || []).map(step => ({
      ...step,
      instruction: cleanInstruction(step.instruction || ''),
    })),
  }));
}


// ── SPOONACULAR ───────────────────────────────────────────────────────────────
async function fetchFromSpoonacular(ingredients) {
  // Proxied through /api/recipes server-side — key never in browser
  const res = await fetch('/api/recipes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ingredients, nonStapleIngredients: splitStaples(ingredients).nonStaple, source: 'spoonacular' }),
  });
  const text = await res.text();
  let data = {};
  try { data = JSON.parse(text); } catch {}
  if (!res.ok) throw new Error(data.error || `Recipe API error ${res.status}`);
  return data.recipes || [];
}

// ── EDAMAM ────────────────────────────────────────────────────────────────────
// Uses Edamam Recipe Search API v2 (app_id + app_key from Meal Planner plan).
// Returns rich cards with real nutrition, ingredient amounts, and smart steps.
async function fetchFromEdamam(ingredients, nonStapleIngredients = [], mealType = null) {
  // Proxied through /api/recipes server-side — Edamam keys never in browser
  const res = await fetch('/api/recipes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ingredients, nonStapleIngredients, mealType }),
  });
  const text = await res.text();
  let data = {};
  try { data = JSON.parse(text); } catch {}
  if (!res.ok) throw new Error(data.error || `Recipe API error ${res.status}`);
  return data.recipes || [];
}


// ── THEMEALDB ─────────────────────────────────────────────────────────────────
async function fetchFromMealsDB(ingredients) {
  const main = ingredients[0];
  const res  = await fetch(
    `https://www.themealdb.com/api/json/v1/1/filter.php?i=${encodeURIComponent(main)}`
  );
  if (!res.ok) throw new Error(`MealDB error: ${res.status}`);
  const data  = await res.json();
  const meals = (data.meals || []).slice(0, 24);

  // Fetch full meal detail for each to get real instructions
  const full = await Promise.all(meals.map(async m => {
    try {
      const r   = await fetch(`https://www.themealdb.com/api/json/v1/1/lookup.php?i=${m.idMeal}`);
      const d   = await r.json();
      return d.meals?.[0] || m;
    } catch { return m; }
  }));

  return full.map(m => {
    const rawInstructions = m.strInstructions || '';
    // Split MealDB's paragraph instructions into sensible steps
    const stepTexts = rawInstructions
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l.length > 20);

    const steps = stepTexts.length > 0
      ? stepTexts.map((text, idx) => ({
          phase: idx < Math.ceil(stepTexts.length * 0.35) ? 'prep' : 'cook',
          instruction: cleanInstruction(text),
          duration: idx === 0 ? '10 min' : idx === stepTexts.length - 1 ? null : '8–12 min',
        }))
      : buildGenericSteps(m.strMeal, ingredients, []);

    return {
      id:          m.idMeal,
      emoji:       '🍽',
      title:       m.strMeal,
      description: `A classic ${m.strArea || ''} dish featuring ${main} as the star ingredient.`.trim(),
      prepTime:    '10 min',
      cookTime:    '25 min',
      totalTime:   '35 min',
      difficulty:  'Medium',
      servings:    '2–4',
      matchPercent: (() => {
        const ingText = rawInstructions.toLowerCase() + ' ' + (m.strTags || '').toLowerCase();
        const count = ingredients.filter(i => ingText.includes(i.toLowerCase())).length;
        return Math.min(99, Math.round((count / Math.max(ingredients.length, 1)) * 100));
      })(),
      ingredientDetails: [
        ...ingredients.slice(0, 3).map(i => ({ name: i, amount: 'as needed', have: true })),
        { name: 'Salt',        amount: '1 tsp',  have: false },
        { name: 'Black pepper',amount: '½ tsp',  have: false },
      ],
      steps,
    };
  });
}

// ── GENERIC STEP BUILDER ──────────────────────────────────────────────────────
// Used as fallback when an API doesn't return detailed step data.
// Generates plausible, ingredient-aware steps rather than useless placeholders.
function buildGenericSteps(title, userIngredients, recipeIngredients) {
  const mainIng = userIngredients[0] || 'the main ingredient';
  const others  = userIngredients.slice(1, 3).join(' and ') || 'remaining ingredients';
  return [
    {
      phase: 'prep',
      instruction: `Gather all ingredients and read the recipe through before starting. Wash and dry any fresh produce. Dice or slice ${mainIng} into even pieces — uniform cuts ensure everything cooks at the same rate and prevents some pieces burning while others stay raw.`,
      duration: '8–10 min',
    },
    {
      phase: 'prep',
      instruction: `Prepare ${others}: measure out any sauces or dry spices into small bowls so they're ready to add quickly once the pan is hot. Having everything prepped before you start cooking ("mise en place") is the single biggest upgrade you can make to your cooking.`,
      duration: '3–5 min',
    },
    {
      phase: 'cook',
      instruction: `Heat a wide, heavy-bottomed pan over medium-high heat until a drop of water evaporates immediately on contact. Add your cooking fat and swirl to coat. Add ${mainIng} in a single layer without crowding — crowding steams rather than sears. Leave undisturbed for 2–3 minutes to build colour before turning.`,
      duration: '5–6 min',
    },
    {
      phase: 'cook',
      instruction: `Add ${others} to the pan. Stir to combine with ${mainIng} and scrape up any caramelised bits from the bottom — this is where all the flavour lives. Reduce heat to medium and continue cooking, stirring occasionally. The mixture should smell fragrant and look glossy.`,
      duration: '5–8 min',
    },
    {
      phase: 'cook',
      instruction: `Season with salt and pepper gradually — taste as you go rather than seasoning all at once. Add any sauces, stock, or liquid called for and bring to a gentle simmer. A simmer means small lazy bubbles; a boil means vigorous large ones. Adjust heat to maintain a steady simmer.`,
      duration: '6–10 min',
    },
    {
      phase: 'cook',
      instruction: `Check for doneness: proteins should be cooked through with no pink, vegetables should be tender but still have a little bite. Taste one final time and adjust seasoning. Remove from heat and let rest for 2 minutes before plating — this lets juices redistribute and the temperature equalise throughout.`,
      duration: '2–3 min',
    },
  ];
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────

// Known staple labels — used to deprioritise staples in query building
const STAPLE_LABELS = new Set([
  'salt','black pepper','butter','olive oil','garlic','onion','sugar','flour',
  'eggs','vinegar','milk','pepper','water','baking soda','baking powder',
]);

function splitStaples(ingredients) {
  const nonStaple = ingredients.filter(i => !STAPLE_LABELS.has(i.toLowerCase()));
  return { nonStaple, all: ingredients };
}

export async function fetchRecipes(ingredients, mealType = null) {
  switch (BACKEND) {
    case 'spoonacular': return fetchFromSpoonacular(ingredients);
    case 'edamam':      return fetchFromEdamam(ingredients, splitStaples(ingredients).nonStaple, mealType);
    case 'mealsdb':     return fetchFromMealsDB(ingredients);
    case 'gemini':
      return fetchFromGemini(ingredients);
    default: {
      // Auto-fallback chain: Edamam → Spoonacular → Gemini → MealsDB
      // All recipe APIs are now server-side — always attempt them
      if (true) {
        try {
          return await fetchFromEdamam(ingredients, splitStaples(ingredients).nonStaple, mealType);
        } catch (e) {
          console.warn('[ohapotato] Edamam failed:', e.message, '— trying Spoonacular');
        }
      }

      if (true) {
        try {
          return await fetchFromSpoonacular(ingredients);
        } catch (e) {
          console.warn('[ohapotato] Spoonacular failed:', e.message, '— trying Gemini');
        }
      }

      // Gemini always available server-side
      if (true) {
        try {
          return await fetchFromGemini(ingredients);
        } catch (e) {
          console.warn('[ohapotato] Gemini failed:', e.message, '— falling back to MealsDB');
        }
      }

      console.info('[ohapotato] All APIs exhausted — using MealsDB (free tier)');
      return fetchFromMealsDB(ingredients);
    }
  }
}
